import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { inject, injectable, symbolToken } from '@servicetitan/react-ioc';

import { AUTH_STORE_TOKEN, IAuthStore } from '../auth';
import { NotificationCenterStore } from '../common';
import {
    ConnectAppRequest,
    ConnectedAppDetailsResponse,
    ConnectedAppResponse,
    ConnectedAppSecretResponse,
    PotentialAppDetailsResponse,
    PotentialAppResponse,
    SecretManagementOption,
    UpdateConnectedAppScopesRequest,
} from '@servicetitan/public-api-app-mgmt';

export interface ThirdPartyAppRequestModel {
    organizationName: string;
    organizationUrl: string;
    contactEmailAddress: string;
    description: string;
}

export interface IAppApi {
    hasAcceptedSiteTerms(): Promise<boolean>;
    acceptSiteTerms(): Promise<void>;
    acceptNewAppTermsOfUse(): Promise<void>;
    requestNewApp(request: ThirdPartyAppRequestModel): Promise<void>;
    getApiConfiguration(): Promise<ApiConfigurationResponse>;
    getApps(): Promise<AppSummaryModel[]>;
    getApp(
        id: number,
        options?: { includeApiScopeGroups?: boolean; includeClients?: boolean },
    ): Promise<AppDetailsResponse>;
    createApp(model: AppDetailsModel): Promise<string>;
    updateApp(id: number, model: AppDetailsModel): Promise<void>;
    deleteApp(id: number): Promise<void>;
    generateClientSecretAsDeveloper(
        id: number,
        environmentId: string,
        clientId: string,
    ): Promise<string>;
    deactivateClientSecretAsDeveloper(
        id: number,
        environmentId: string,
        clientId: string,
        secretId: string,
    ): Promise<void>;
    getApiUsageReport(id: number, startDate: Date, endDate: Date): Promise<ApiUsageReportResponse>;
    getWebhookEvents(id: number): Promise<WebhookEventStatusesSummaryModel>;
    enableWebhookEvent(id: number, webhookEventName: string): Promise<void>;
    getPotentialApps(apiGroupName: string, owner: string): Promise<PotentialAppResponse[]>;
    getPotentialAppDetails(
        apiGroupName: string,
        owner: string,
        publicId: string,
        scopesVersion: number,
    ): Promise<PotentialAppDetailsResponse>;
    acceptClientsTerms(apiGroupName: string, owner: string, publicId: string): Promise<void>;
    connectPotentialApp(
        apiGroupName: string,
        owner: string,
        request: ConnectAppRequest,
    ): Promise<string>;
    getConnectedApps(apiGroupName: string, owner: string): Promise<ConnectedAppResponse[]>;
    getConnectedApp(
        apiGroupName: string,
        owner: string,
        clientId: string,
    ): Promise<ConnectedAppDetailsResponse>;
    updateConnectedAppScopes(
        apiGroupName: string,
        owner: string,
        clientId: string,
        request: UpdateConnectedAppScopesRequest,
    ): Promise<void>;
    generateClientSecret(apiGroupName: string, owner: string, clientId: string): Promise<string>;
    deactivateClientSecret(
        apiGroupName: string,
        owner: string,
        clientId: string,
        secretId: string,
    ): Promise<void>;
    disableConnectedApps(
        apiGroupName: string,
        owner: string,
        connectedAppIds: string[],
    ): Promise<void>;
    enableConnectedApps(
        apiGroupName: string,
        owner: string,
        connectedAppIds: string[],
    ): Promise<void>;
    deleteConnectedApps(
        apiGroupName: string,
        owner: string,
        connectedAppIds: string[],
    ): Promise<void>;
}

export const APP_API_TOKEN = symbolToken<IAppApi>('APP_API_TOKEN');

function formatYYYYMMDD(date: Date): string {
    return [
        date.getFullYear().toString(),
        ('0' + (date.getMonth() + 1).toString()).slice(-2),
        ('0' + date.getDate().toString()).slice(-2),
    ].join('-');
}

@injectable()
export class AppApi implements IAppApi {
    private readonly axios = axios.create();
    private cachedApiConfiguration: ApiConfigurationResponse | null = null;
    private cachedApps: AppSummaryModel[] | null = null;

    constructor(
        @inject(AUTH_STORE_TOKEN) private readonly authStore: IAuthStore,
        @inject(NotificationCenterStore)
        private readonly notificationCenterStore: NotificationCenterStore,
    ) {
        this.axios.interceptors.response.use(undefined, (error: AxiosError) => {
            if (
                error.response?.status === 400 &&
                typeof error.response.data === 'string' &&
                error.response.data
            ) {
                this.notificationCenterStore.addToast({
                    status: 'critical',
                    title: 'Failed Validation',
                    message: error.response.data,
                });
            } else {
                this.notificationCenterStore.addToast({
                    status: 'critical',
                    title: 'Error',
                    message: error.message,
                });
            }
            return Promise.reject(error);
        });
    }

    async hasAcceptedSiteTerms(): Promise<boolean> {
        const response = await this.get<boolean>('/terms/accepted');
        return response.data;
    }

    async acceptSiteTerms(): Promise<void> {
        await this.put('/terms/accepted', true);
    }

    async acceptNewAppTermsOfUse(): Promise<void> {
        await this.post('/terms/new-app-terms-accepted', {});
    }

    async requestNewApp(request: ThirdPartyAppRequestModel): Promise<void> {
        await this.post('/app-requests', request);
    }

    async getApiConfiguration(): Promise<ApiConfigurationResponse> {
        if (this.cachedApiConfiguration === null) {
            const response =
                await this.get<Omit<ApiConfigurationResponse, 'getApiScopeDisplayNames'>>(
                    '/api-configuration',
                );
            this.cachedApiConfiguration = response.data;
        }
        return this.cachedApiConfiguration;
    }

    async getApps(): Promise<AppSummaryModel[]> {
        if (this.cachedApps === null) {
            const response = await this.get<AppSummaryModel[]>('/apps');
            this.cachedApps = response.data;
        }
        return this.cachedApps;
    }

    async getApp(
        id: number,
        {
            includeApiScopeGroups,
            includeClients,
        }: { includeApiScopeGroups?: boolean; includeClients?: boolean } = {},
    ): Promise<AppDetailsResponse> {
        const params = new URLSearchParams();
        if (includeApiScopeGroups) {
            params.append('includeApiScopeGroups', 'true');
        }
        if (includeClients) {
            params.append('includeClients', 'true');
        }
        const queryString = params.toString();
        const response = await this.get<AppDetailsResponse>(
            `/apps/${id}${queryString ? `?${queryString}` : ''}`,
        );
        return response.data;
    }

    async createApp(model: AppDetailsModel): Promise<string> {
        const response = await this.post('/apps', model);
        this.cachedApps = null;
        return response.headers.location!;
    }

    async updateApp(id: number, model: AppDetailsModel): Promise<void> {
        await this.put(`/apps/${id}`, model);
        this.cachedApps = null;
    }

    async deleteApp(id: number): Promise<void> {
        await this.delete(`/apps/${id}`);
        this.cachedApps = null;
    }

    async generateClientSecretAsDeveloper(
        id: number,
        environmentId: string,
        clientId: string,
    ): Promise<string> {
        const response = await this.post<string>(
            `/apps/${id}/environments/${environmentId}/clients/${clientId}/secrets`,
        );
        return response.data;
    }

    async deactivateClientSecretAsDeveloper(
        id: number,
        environmentId: string,
        clientId: string,
        secretId: string,
    ): Promise<void> {
        await this.delete(
            `/apps/${id}/environments/${environmentId}/clients/${clientId}/secrets/${secretId}`,
        );
    }

    async getApiUsageReport(
        id: number,
        startDate: Date,
        endDate: Date,
    ): Promise<ApiUsageReportResponse> {
        const response = await this.get<ApiUsageReportResponse>(
            `/apps/${id}/api-usage-report?startDate=${formatYYYYMMDD(
                startDate,
            )}&endDate=${formatYYYYMMDD(endDate)}`,
        );
        return response.data;
    }

    async getWebhookEvents(id: number): Promise<WebhookEventStatusesSummaryModel> {
        const response = await this.get<WebhookEventStatusesSummaryModel>(
            `/apps/${id}/webhook-events`,
        );
        return response.data;
    }

    async enableWebhookEvent(id: number, webhookEventName: string): Promise<void> {
        await this.post(`/apps/${id}/webhook-events/${webhookEventName}/enable`);
    }

    async getPotentialApps(apiGroupName: string, owner: string): Promise<PotentialAppResponse[]> {
        const response = await this.get<PotentialAppResponse[]>(
            `/potential-apps/${apiGroupName}/${owner}`,
        );
        return response.data;
    }

    async getPotentialAppDetails(
        apiGroupName: string,
        owner: string,
        publicId: string,
        scopesVersion: number,
    ): Promise<PotentialAppDetailsResponse> {
        const response = await this.get<PotentialAppDetailsResponse>(
            `/potential-apps/${apiGroupName}/${owner}/${publicId}?scopesVersion=${scopesVersion}`,
        );
        return response.data;
    }

    async acceptClientsTerms(apiGroupName: string, owner: string, publicId: string): Promise<void> {
        await this.put(
            `/potential-apps/${apiGroupName}/${owner}/${publicId}/clients-terms/accepted`,
            true,
        );
    }

    async connectPotentialApp(
        apiGroupName: string,
        owner: string,
        request: ConnectAppRequest,
    ): Promise<string> {
        const response = await this.post<string, ConnectAppRequest>(
            `/clients/${apiGroupName}/${owner}`,
            request,
        );
        return response.data;
    }

    async getConnectedApps(apiGroupName: string, owner: string): Promise<ConnectedAppResponse[]> {
        const response = await this.get<ConnectedAppResponse[]>(
            `/clients/${apiGroupName}/${owner}`,
        );
        return response.data;
    }

    async getConnectedApp(
        apiGroupName: string,
        owner: string,
        clientId: string,
    ): Promise<ConnectedAppDetailsResponse> {
        const response = await this.get<ConnectedAppDetailsResponse>(
            `/clients/${apiGroupName}/${owner}/${clientId}`,
        );
        return response.data;
    }

    async updateConnectedAppScopes(
        apiGroupName: string,
        owner: string,
        clientId: string,
        request: UpdateConnectedAppScopesRequest,
    ): Promise<void> {
        await this.put<void, UpdateConnectedAppScopesRequest>(
            `/clients/${apiGroupName}/${owner}/${clientId}/scopes`,
            request,
        );
    }

    async generateClientSecret(
        apiGroupName: string,
        owner: string,
        clientId: string,
    ): Promise<string> {
        const response = await this.post<string>(
            `/clients/${apiGroupName}/${owner}/${clientId}/secrets`,
        );
        return response.data;
    }

    async deactivateClientSecret(
        apiGroupName: string,
        owner: string,
        clientId: string,
        secretId: string,
    ): Promise<void> {
        await this.delete(`/clients/${apiGroupName}/${owner}/${clientId}/secrets/${secretId}`);
    }

    async disableConnectedApps(
        apiGroupName: string,
        owner: string,
        connectedAppIds: string[],
    ): Promise<void> {
        await this.post<void, string[]>(
            `/clients/${apiGroupName}/${owner}/disable`,
            connectedAppIds,
        );
    }

    async enableConnectedApps(
        apiGroupName: string,
        owner: string,
        connectedAppIds: string[],
    ): Promise<void> {
        await this.post<void, string[]>(
            `/clients/${apiGroupName}/${owner}/enable`,
            connectedAppIds,
        );
    }

    async deleteConnectedApps(
        apiGroupName: string,
        owner: string,
        connectedAppIds: string[],
    ): Promise<void> {
        await this.post<void, string[]>(
            `/clients/${apiGroupName}/${owner}/delete`,
            connectedAppIds,
        );
    }

    private get<T>(url: string): Promise<AxiosResponse<T>> {
        return this.axios.get<T>(url, this.getConfig());
    }

    private post<T = void, D = void>(url: string, data?: D): Promise<AxiosResponse<T>> {
        return this.axios.post(url, data, this.getConfig());
    }

    private put<T = void, D = void>(url: string, data?: D): Promise<AxiosResponse<T>> {
        return this.axios.put(url, data, this.getConfig());
    }

    private delete<T = void>(url: string): Promise<AxiosResponse<T>> {
        return this.axios.delete(url, this.getConfig());
    }

    private getConfig(): AxiosRequestConfig {
        return {
            headers: {
                'Accept': 'application/json',
                'Authorization': this.authStore.accessToken
                    ? `Bearer ${this.authStore.accessToken}`
                    : undefined,
                'Cache-Control': 'no-cache',
                'Content-Type': 'application/json',
            },
        };
    }
}

export interface ApiConfigurationResponse {
    developerType: DeveloperType;
    apiGroups: ApiGroup[];
    appCategories: AppCategory[];
    devOnly: boolean;
    autoApproveCustomerApps: boolean;
}

export enum DeveloperType {
    ThirdParty = 0,
    Customer = 1,
    FirstParty = 2,
}

export enum AppApprovalStatus {
    NotStarted = 0,
    EvaluationInProgress = 1,
    NegotiatingPartnershipAgreement = 2,
    Rejected = 3,
    Approved = 4,
}

export enum AppVersionApprovalStatus {
    NotStarted = 0,
    Rejected = 3,
    Approved = 4,
}

export enum AppReviewStatus {
    EvaluationInProgress = 0,
    Rejected = 1,
    Approved = 2,
}

export enum AppVersionApprovalStatusReason {
    WithinApprovedUseCases = 1,
    NotWithinApprovedUseCases = 11,
    NewerVersionSubmitted = 12,
    Other = 19,
}

export enum AppBuilder {
    Customer = 0,
    Contractor = 1,
    ThirdParty = 2,
}

export interface ApiGroup {
    name: string;
    displayName: string;
    scopeGroups: ApiScopeGroup[];
    resourceOwnerTerm: string;
    resourceOwnerTermPlural: string;
    assignedResourceOwners: string[];
    maxNetworkCount: number;
}

export interface ApiScopeGroup {
    name: string;
    displayName: string;
    scopes: ApiScope[];
    webhookEvents: WebhookEvent[];
}

export interface ApiScope {
    name: string;
    displayName: string;
    accessible: boolean;
    hasRead: boolean;
    hasWrite: boolean;
    canRead: boolean;
    canWrite: boolean;
}

export interface WebhookEvent {
    name: string;
    displayName: string;
    accessible: boolean;
}

export interface AppCategory {
    id: number;
    name: string;
    children: AppCategory[];
}

export interface AppAvailability {
    resourceOwner: string;
    note: string;
}

export const EmptyAppAvailability: AppAvailability = { resourceOwner: '', note: '' };

export interface AppSummaryModel {
    id: number;
    appName: string;
    apiGroupName: string;
    currentScopesVersion: AppScopesVersionModel;
    deleted: boolean;
    reviewStatus: AppReviewStatus;
}

export interface AppDetailsModel {
    appName: string;
    organizationName: string;
    homepageUrl: string;
    emailAddress: string | null;
    description: string;
    appCategoryId: number | null;
    appBuilder: AppBuilder | null;
    externalDataGuid: string;
    apiGroupName: string;
    authScopes: string[];
    webhookEvents: WebhookEventModel[];
    webhookSignatureKey: string | null;
    appAvailabilities: AppAvailability[];
    networkAppAvailabilities: AppAvailability[];
    secretManagementOption: SecretManagementOption;
}

export interface AppDetailsResponse extends AppDetailsModel {
    publicId: string;
    apiScopeGroups: ApiScopeGroup[];
    scopesVersions: AppScopesVersionModel[];
    applicationKey1: string;
    clients: AppClientResponse[];
    createdOn: Date;
    apiUsageReportMinDate: Date;
    deleted: boolean;
    approvalStatus: AppApprovalStatus;
}

export interface AppScopesVersionModel {
    version: number;
    dateTime: Date;
    userName: string;
    authScopes: AuthScopeDisplayModel[];
    webhookEvents: WebhookEventDisplayModel[];
    approvalStatus: AppVersionApprovalStatus;
    approvalStatusReason: AppVersionApprovalStatusReason | null;
    reviewedBy: string | null;
    reviewedOn: Date | null;
}

export interface AuthScopeDisplayModel {
    displayName: string;
    accessible: boolean;
    read: boolean;
    write: boolean;
}

export interface WebhookEventModel {
    name: string;
    url: string;
}

export interface WebhookEventDisplayModel {
    displayName: string;
    accessible: boolean;
}

export interface WebhookEventStatusesSummaryModel {
    webhookEventsWithStatus: WebhookEventModelWithStatus[];
}

export interface WebhookEventModelWithStatus extends WebhookEventModel {
    displayName: string;
    accessible: boolean;
    status: string;
}

export interface AppClientResponse {
    environmentId: string | null;
    environmentName: string | null;
    resourceOwnerTerm: string | null;
    resourceOwner: string;
    resourceOwnerDetails: string | null;
    clientId: string | null;
    clientSecrets: ConnectedAppSecretResponse[];
    currentScopesVersion: number;
    enabled: boolean;
}

export { ConnectedAppSecretResponse, SecretManagementOption };

export interface ApiUsageReportResponse {
    firstBinDateTime: string; // YYYYMMDDHHMM
    lastBinDateTime: string; // YYYYMMDDHHMM
    endpoints: {
        apiName: string;
        httpMethod: string;
        endpointName: string;
    }[];
    tenants: {
        [id: string]: {
            name: string;
            successfulCallCounts: number[];
        };
    };
}
